/* eslint-disable react/jsx-no-literals */
/* eslint-disable react/jsx-sort-props */
/* eslint-disable sort-keys-fix/sort-keys-fix */
//
// Role-aware dashboard (Option B). Cards are gated by account_type so each
// role lands on what they need to act on — not a generic KPI wall.
//
// Layout (4 row stack):
//   1. KPI row     — universal, 4 numbers
//   2. Action inbox — role-driven priorities ("what needs you today")
//   3. Pipeline + Top campaigns — visible to OWNER/ADMIN + NV TD
//   4. Recent activity + shortcuts
//
// Data sources are 100% existing endpoints; no BE work. Each card degrades
// to a "—" placeholder when the source query fails or returns empty so a
// single bad endpoint doesn't blank the whole page.
import {
  ArrowRightOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Card, Col, Progress, Row, Space, Statistic, Tag, Typography } from 'antd';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { axiosClient } from '@/api/axiosClient';
import { useAppSelector } from '@/hooks';
import { selectCurrentUser } from '@/modules/auth/core/authSlice';
import { HRRole } from '@/modules/hr-management/models';

// ─── helpers ───────────────────────────────────────────────────────────────
// axiosClient returns the full envelope {status, message, data}. Use this
// helper everywhere so we don't fall into the .data trap again.
function unwrap<T>(resp: unknown): T | undefined {
  return (resp as { data?: T } | undefined)?.data;
}

interface Paged<T> {
  rows: T[];
  total_rows: number;
}

function useCount(key: string, url: string): number {
  const { data } = useQuery(
    ['dash-count', key, url],
    () => axiosClient.get<Paged<unknown>>(url),
    { refetchOnWindowFocus: false, staleTime: 60_000 }
  );

  return unwrap<Paged<unknown>>(data)?.total_rows ?? 0;
}

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  COMPLETED: 'blue',
  PENDING: 'orange',
};

// ─── page ──────────────────────────────────────────────────────────────────
const DashboardPage = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const accountType = currentUser?.account_type;
  const isPrivileged = accountType === HRRole.owner || accountType === HRRole.admin;

  // KPI numbers — always visible.
  const totalWorkers = useCount('w', '/worker/filter?page_size=1&page_index=1');
  const totalPlans = useCount('p', '/recruitment-plan/filter-plans?page_size=1&page_index=1');
  const totalCustomers = useCount('c', '/customer/filter?page=1&limit=1');

  // RP-16 ticket totals — for the "Quá hạn > 24h" KPI.
  const { data: rp16 } = useQuery(
    ['dash-rp16'],
    () => axiosClient.get('/report/RP-16'),
    { refetchOnWindowFocus: false, staleTime: 60_000 }
  );
  const overdueTickets =
    (unwrap<{ totals?: { overdue?: number } }>(rp16))?.totals?.overdue ?? 0;

  // Action inbox queries — applications / leave / waiting workers.
  const pendingApps = useCount('a', '/application/filter?status=PENDING&page=1&limit=1');
  const pendingLeaves = useCount('l', '/leave-request/filter?status=PENDING&page=1&limit=1');
  const waitingWorkers = useCount(
    'ww',
    '/worker/filter?operational_status_id=TRANG-THAI-VAN-HANH-NLD-02&page_size=1&page_index=1'
  );

  // RP-12 funnel (pipeline)
  const { data: rp12 } = useQuery(
    ['dash-rp12'],
    () => axiosClient.get('/report/RP-12'),
    {
      enabled: isPrivileged,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    }
  );
  const funnelRows =
    (unwrap<{ rows: Array<{ step: string; count: number; pct_of_top: number }> }>(rp12))?.rows ??
    [];

  // RP-03 top campaigns by completion
  const { data: rp03 } = useQuery(
    ['dash-rp03'],
    () => axiosClient.get('/report/RP-03'),
    {
      enabled: isPrivileged,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    }
  );
  const topCampaigns = (
    unwrap<{
      rows: Array<{
        plan_code: string;
        plan_name: string;
        customer: string;
        rate_pct: number;
      }>;
    }>(rp03))?.rows ?? [];
  const topSorted = [...topCampaigns]
    .sort((a, b) => (b.rate_pct ?? 0) - (a.rate_pct ?? 0))
    .slice(0, 5);
  const bottomSorted = [...topCampaigns]
    .sort((a, b) => (a.rate_pct ?? 0) - (b.rate_pct ?? 0))
    .slice(0, 5);

  // Activity feed — recruitment-plan audit needs a plan_id so we can't show
  // a global feed without a new BE endpoint. Instead surface the 5 most
  // recent applications: same intent ("what just happened") + every prod
  // env has the endpoint + data.
  const { data: recentApps } = useQuery(
    ['dash-recent-apps'],
    () => axiosClient.get('/application/filter?page=1&limit=5'),
    {
      enabled: isPrivileged,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    }
  );
  const recentAppRows =
    (unwrap<{
      rows: Array<{
        id: string;
        status: string;
        submitted_at: string;
        worker_name?: string;
        plan_name?: string;
      }>;
    }>(recentApps))?.rows ?? [];

  return (
    <div className="p-4">
      <Typography.Title level={3} style={{ marginBottom: 4 }}>
        Xin chào, {currentUser?.name || 'bạn'} 👋
      </Typography.Title>
      <Typography.Text type="secondary">
        {accountType} • {moment().format('dddd, DD/MM/YYYY')}
      </Typography.Text>

      {/* ─────────── KPI row ─────────── */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={6}>
          <Link to="/worker/list">
            <Card hoverable>
              <Statistic
                title="NLĐ đang làm"
                value={totalWorkers}
                prefix={<TeamOutlined rev="" />}
              />
            </Card>
          </Link>
        </Col>
        <Col span={6}>
          <Link to="/recruitment-plan/list">
            <Card hoverable>
              <Statistic title="Chiến dịch" value={totalPlans} />
            </Card>
          </Link>
        </Col>
        <Col span={6}>
          <Link to="/customer/list">
            <Card hoverable>
              <Statistic title="Khách hàng" value={totalCustomers} />
            </Card>
          </Link>
        </Col>
        <Col span={6}>
          <Link to="/report?id=RP-16">
            <Card hoverable>
              <Statistic
                title="Ticket quá hạn (>24h)"
                value={overdueTickets}
                valueStyle={{ color: overdueTickets > 0 ? '#cf1322' : undefined }}
                prefix={<ExclamationCircleOutlined rev="" />}
              />
            </Card>
          </Link>
        </Col>
      </Row>

      {/* ─────────── Action inbox ─────────── */}
      <Card
        style={{ marginTop: 16 }}
        title={
          <span>
            <ClockCircleOutlined rev="" /> Cần bạn hành động hôm nay
          </span>
        }
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <ActionItem
            count={pendingApps}
            label="đơn ứng tuyển PENDING"
            to="/application"
            tone="processing"
          />
          <ActionItem
            count={pendingLeaves}
            label="đơn nghỉ phép chờ duyệt"
            to="/leave-request"
            tone="processing"
          />
          <ActionItem
            count={waitingWorkers}
            label="NLĐ đang Chờ việc — kiểm tra ngày auto-resign (V1.4 B5)"
            to="/worker/list?operational_status_id=TRANG-THAI-VAN-HANH-NLD-02"
            tone={waitingWorkers > 0 ? 'warning' : 'default'}
          />
          <ActionItem
            count={overdueTickets}
            label="ticket CSKH quá hạn"
            to="/communication/worker-support"
            tone={overdueTickets > 0 ? 'error' : 'default'}
          />
        </Space>
      </Card>

      {/* ─────────── Pipeline + Top campaigns (OWNER/ADMIN) ─────────── */}
      {isPrivileged && (
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card title="Pipeline tuyển dụng (RP-12)">
              {funnelRows.length === 0 && (
                <Typography.Text type="secondary">Chưa có data</Typography.Text>
              )}
              {funnelRows.map((step) => (
                <div key={step.step} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{step.step}</span>
                    <strong>{step.count}</strong>
                  </div>
                  <Progress
                    percent={step.count === 0 ? 0 : step.pct_of_top}
                    showInfo={false}
                    strokeColor="#1890ff"
                  />
                </div>
              ))}
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Chiến dịch — Top 5 hoàn thành (RP-03)">
              {topSorted.length === 0 && (
                <Typography.Text type="secondary">Chưa có data</Typography.Text>
              )}
              {topSorted.map((p) => (
                <CampaignRow key={p.plan_code} plan={p} />
              ))}
              {bottomSorted.length > 0 && (
                <>
                  <Typography.Title level={5} style={{ marginTop: 24 }}>
                    Bottom 5 — cần chú ý
                  </Typography.Title>
                  {bottomSorted.map((p) => (
                    <CampaignRow key={p.plan_code} plan={p} warn />
                  ))}
                </>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* ─────────── Recent activity + shortcuts ─────────── */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        {isPrivileged && (
          <Col span={16}>
            <Card title="Đơn ứng tuyển mới (5 gần nhất)">
              {recentAppRows.length === 0 && (
                <Typography.Text type="secondary">Chưa có đơn nào</Typography.Text>
              )}
              {recentAppRows.map((row) => (
                <div key={row.id} style={{ marginBottom: 8 }}>
                  <Tag color={STATUS_COLOR[row.status] ?? 'blue'}>{row.status}</Tag>
                  <span style={{ marginRight: 8 }}>{row.worker_name || '(no name)'}</span>
                  <span style={{ color: '#666', fontSize: 12 }}>→ {row.plan_name || '?'}</span>
                  <span style={{ color: '#999', float: 'right', fontSize: 12 }}>
                    {moment(row.submitted_at).fromNow()}
                  </span>
                </div>
              ))}
            </Card>
          </Col>
        )}

        <Col span={isPrivileged ? 8 : 24}>
          <Card title="Shortcut">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Link to="/customer/list">
                <ShortcutItem label="Tạo khách hàng mới" />
              </Link>
              <Link to="/recruitment-plan/list">
                <ShortcutItem label="Tạo chiến dịch tuyển dụng" />
              </Link>
              <Link to="/worker/list">
                <ShortcutItem label="Thêm NLĐ" />
              </Link>
              <Link to="/notification">
                <ShortcutItem label="Đẩy thông báo" />
              </Link>
              <Link to="/factory/list">
                <ShortcutItem label="Tạo nhà máy" />
              </Link>
              <Link to="/report">
                <ShortcutItem label="Xem 17 báo cáo" />
              </Link>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// ─── small components ──────────────────────────────────────────────────────
const ActionItem = ({
  count,
  label,
  to,
  tone,
}: {
  count: number;
  label: string;
  to: string;
  tone: 'default' | 'processing' | 'warning' | 'error';
}) => {
  const toneColor: Record<typeof tone, string> = {
    default: '#999',
    error: '#cf1322',
    processing: '#1890ff',
    warning: '#faad14',
  };

  return (
    <Link to={to} style={{ color: 'inherit' }}>
      <div
        style={{
          alignItems: 'center',
          backgroundColor: count > 0 ? '#fafafa' : undefined,
          borderLeft: count > 0 ? `4px solid ${toneColor[tone]}` : '4px solid transparent',
          borderRadius: 4,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 12px',
        }}
      >
        <span>
          <strong style={{ color: toneColor[tone] }}>{count}</strong> {label}
        </span>
        <ArrowRightOutlined rev="" style={{ color: '#999' }} />
      </div>
    </Link>
  );
};

const CampaignRow = ({
  plan,
  warn,
}: {
  plan: { plan_code: string; plan_name: string; customer: string; rate_pct: number };
  warn?: boolean;
}) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>
        <Tag color={warn ? 'red' : 'green'}>{plan.plan_code}</Tag>
        {plan.plan_name}
        <span style={{ color: '#999', fontSize: 12, marginLeft: 6 }}>{plan.customer}</span>
      </span>
      <strong style={{ color: warn ? '#cf1322' : '#52c41a' }}>{plan.rate_pct ?? 0}%</strong>
    </div>
    <Progress
      percent={plan.rate_pct ?? 0}
      showInfo={false}
      size="small"
      strokeColor={warn ? '#cf1322' : '#52c41a'}
    />
  </div>
);

const ShortcutItem = ({ label }: { label: string }) => (
  <div
    style={{
      alignItems: 'center',
      backgroundColor: '#fafafa',
      borderRadius: 4,
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 12px',
    }}
  >
    <span>
      <PlusOutlined rev="" style={{ marginRight: 8 }} />
      {label}
    </span>
    <ArrowRightOutlined rev="" style={{ color: '#999' }} />
  </div>
);

export default DashboardPage;
